/**
 * Where an audit row's references point.
 *
 * A trail row names records in other domains — the bag a transaction produced,
 * the patient it belongs to, the shelf it was put on, the form definition it
 * was written against. Those arrive as `links` entries (`entityType` +
 * `entityId`) and as projected columns (`din`, `subjectKey`, `storageNodeId`,
 * …). This module maps each of them to a route, so a reviewer can follow one
 * instead of copying an id and going to find it by hand.
 *
 * Two rules shape everything here:
 *
 * **A reference with nowhere to go stays text.** `resolve` returns null rather
 * than a guess, and the caller renders exactly what it renders today. A dead
 * link in a compliance record is worse than a plain string — it reads as a
 * record that exists somewhere, and it does not.
 *
 * **The target carries the permissions that open it**, taken from the route's
 * own guard in `src/router/index.tsx`. `usePermission().canAny` decides, the
 * same call the guard makes, so a link never offers a page that will bounce.
 *
 * Pure and dependency-free: `vitest.config.ts` runs `.test.ts` under `src` in a
 * node environment, so the mapping is testable and the components stay dumb.
 * ClickUp z8nrz7dxeu.
 */

import { PERMISSIONS as P } from '@/lib/permissions'
import type { AuditRecordRef, AuditTrailLink, AuditTrailRow } from '@/types/audit'
import { auditStorageNode } from '@/lib/auditTrail'

export interface AuditLinkTarget {
  /** The route to navigate to. */
  to: string
  /**
   * The permissions that open it — ANY one is enough, matching the router's
   * own `canAny`. Empty means the route carries no permission guard.
   */
  permissions: readonly string[]
}

/**
 * A storage POSITION cannot be addressed directly.
 *
 * `/inventory/storage/:unitId` wants the root unit, and a link names a node
 * somewhere inside it ("LN2-A/R1/B1/A1"). Resolving one to the other needs the
 * node and the tenant's roots, which is a fetch — so the link points at a
 * redirect route that does the lookup, and this module stays pure.
 */
export const STORAGE_NODE_ROUTE = '/inventory/storage/node'

/**
 * An entity reference from `links`.
 *
 * `process_step_execution` and `user` are deliberately absent: neither has a
 * detail surface. `/users` is a list, and landing a reviewer on a list they
 * still have to search through is not the same as answering their question.
 */
export function entityTarget(entityType: string, entityId: string): AuditLinkTarget | null {
  if (!entityId) return null
  switch (entityType) {
    case 'product':
      return { to: `/products/${entityId}`, permissions: [P.inventoryView] }
    case 'storage_node':
      return { to: `${STORAGE_NODE_ROUTE}/${entityId}`, permissions: [P.inventoryView] }
    case 'form_definition':
      return { to: `/form-builder/${entityId}`, permissions: [P.formsCreate, P.formsEdit] }
    case 'procedure_template':
      return {
        to: `/procedure-templates/${entityId}/sequence`,
        permissions: [P.procedureTemplatesManage],
      }
    default:
      return null
  }
}

/** The target for one `links` entry. */
export function linkTarget(link: AuditTrailLink): AuditLinkTarget | null {
  return entityTarget(link.entityType, link.entityId)
}

/** How a `links` entry reads when there is no label. */
export function linkLabel(link: AuditTrailLink): string {
  return link.entityLabel || `${link.entityType} ${link.entityId}`
}

// ─── Column targets ─────────────────────────────────────────────────────────
//
// The dedicated columns name the same records the `links` column does, and are
// the ones a reviewer actually scans — Related sits at the far right and is
// read least. Each reads the row's own projected fields rather than hunting
// through `links`, because that is where the server puts them.

/** The product a row is about. DIN and Product code both point here. */
export function productTarget(row: Pick<AuditTrailRow, 'productId'>): AuditLinkTarget | null {
  return row.productId ? entityTarget('product', row.productId) : null
}

/** The storage position a row touched, via the redirect route. */
export function storageTarget(row: AuditTrailRow): AuditLinkTarget | null {
  const { id } = auditStorageNode(row)
  return id ? entityTarget('storage_node', id) : null
}

export function submissionTarget(
  row: Pick<AuditTrailRow, 'submissionId'>,
): AuditLinkTarget | null {
  return row.submissionId
    ? { to: `/forms/${row.submissionId}`, permissions: [P.submissionsRead] }
    : null
}

/**
 * The subject of the row — whatever it happens to be.
 *
 * `subjectKeyType` is one of four (`mrn`, `kit`, `wo`, `cln`) and only the
 * first names a record addressable by the key itself. A kit run and an MES
 * order are procedures: their routes take the row's `procedureId`, not the
 * KIT ID or WO # printed in the column. `cln` (cleaning) has no detail route
 * at all.
 */
export function subjectTarget(
  row: Pick<AuditTrailRow, 'subjectKey' | 'subjectKeyType' | 'procedureId'>,
): AuditLinkTarget | null {
  if (!row.subjectKey) return null
  switch (row.subjectKeyType) {
    case 'mrn':
      return {
        to: `/patients/${encodeURIComponent(row.subjectKey)}`,
        permissions: [P.submissionsRead],
      }
    case 'kit':
      return row.procedureId
        ? { to: `/kits/runs/${row.procedureId}`, permissions: [P.inventoryView] }
        : null
    case 'wo':
      // No permission guard on /mes/orders/:procedureId — it is a plain route.
      return row.procedureId ? { to: `/mes/orders/${row.procedureId}`, permissions: [] } : null
    default:
      return null
  }
}

/**
 * The page of an Audit Trail Center row's record, for a record with no audit
 * trail of its own to open in the pop-up: a form or procedure template, a kit
 * run or MES order, a pick list, a storage transfer, an integration. Null for
 * a record with no page that addresses it by id (a material, a zone, a user).
 */
export function recordTarget(
  record: AuditRecordRef | null,
  row: Pick<AuditTrailRow, 'subjectKey' | 'subjectKeyType'> & { procedureDeleted?: boolean },
): AuditLinkTarget | null {
  if (!record) return null
  switch (record.kind) {
    case 'form_definition':
    case 'procedure_template':
      return entityTarget(record.kind, record.id)
    case 'procedure':
      // A kit run or an MES order, told apart by the key it is known by —
      // unless it has since been deleted, and its page would find nothing.
      if (row.procedureDeleted) return null
      return row.subjectKeyType === 'kit' || row.subjectKeyType === 'wo'
        ? subjectTarget({ subjectKey: row.subjectKey ?? record.label, subjectKeyType: row.subjectKeyType, procedureId: record.id })
        : null
    case 'pick_list':
      return { to: `/inventory/pick-lists/${record.id}`, permissions: [P.inventoryView] }
    case 'transfer':
      return { to: `/inventory/storage-transfers/${record.id}`, permissions: [P.storageReplace] }
    case 'integration':
      return { to: `/integrations/${record.id}`, permissions: [P.integrationsRead] }
    default:
      return null
  }
}
